import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Typography,
    Checkbox,
    Box,
    Skeleton,
    ImageList,
    ImageListItem,
    useTheme,
    useMediaQuery,
    TextField,
    Tooltip,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ZoomableImage from './zoomableimage';
import { useTranslation } from 'react-i18next';

const SkeletonLoader = ({ width = 150, height = 150 }) => (
    <Skeleton variant="rectangular" width={width} height={height} animation="wave" />
);

export default function ImageListAccordion({
    pictures,
    imageUrls,
    imageMarkedToPdf,
    onImageMarkedToPdf,
    onImageCommentChange,
}) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    let imageListCols = 1;

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    if (isTablet) {
        imageListCols = 2;
    } else if (!isMobile) {
        imageListCols = 3;
    }

    const thumbStyle = {
        maxWidth: '100%',
        height: 'auto',
    };

    return (
        <Accordion expanded={open} onChange={() => setOpen(!open)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="picture-accordion">
                <Stack direction="row" spacing={1} alignItems="center">
                    <PhotoCameraIcon />
                    <h5>
                        {t('survey.imageWithCount', {
                            pcs: pictures ? pictures.length : 0,
                        })}
                    </h5>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {pictures && pictures.length > 0 ? (
                    <ImageList
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gap: 16,
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        }}
                        cols={imageListCols}>
                        {pictures.map((picture, pictureIndex) => {
                            const existingComment = picture.comments || '';
                            const maxCommentLength = Math.max(60, existingComment.length);
                            const phrases = Array.isArray(picture.phrases)
                                ? picture.phrases
                                : Array.isArray(picture.filedPhrases)
                                  ? picture.filedPhrases
                                  : [];
                            const phraseObservations = phrases.map((phrase) => phrase.observation).filter(Boolean);
                            const hasObservations = phraseObservations.length > 0;
                            const observationTooltip =
                                phraseObservations.length <= 1 ? (
                                    phraseObservations[0]
                                ) : (
                                    <Box sx={{ maxWidth: 320 }}>
                                        {phraseObservations.map((observation, index) => (
                                            <Typography key={`${picture.imageId}-observation-${index}`} variant="body2">
                                                • {observation}
                                            </Typography>
                                        ))}
                                    </Box>
                                );

                            return (
                                <ImageListItem
                                    key={picture.imageId + pictureIndex}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        paddingBottom: 2,
                                    }}>
                                    {!imageUrls[picture.imageId] ? (
                                        <SkeletonLoader />
                                    ) : (
                                        <ZoomableImage
                                            thumbUrl={imageUrls[picture.imageId].thumbUrl}
                                            fullResUrl={imageUrls[picture.imageId].fullResUrl}
                                            thumbStyle={thumbStyle}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: 3,
                                            width: 'auto',
                                            padding: 1,
                                            paddingY: 1.5,
                                            backgroundColor: 'transparent',
                                        }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <TextField
                                                label={t('survey.comments')}
                                                data-testid={`picture-${picture.imageId}-comment`}
                                                defaultValue={existingComment}
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: '200px',
                                                    resize: 'vertical',
                                                }}
                                                inputProps={{ maxLength: maxCommentLength }}
                                                // Intentionally using onBlur instead of onChange to avoid UI lag when many items are rendered.
                                                onBlur={(e) => {
                                                    onImageCommentChange(e, picture.imageId);
                                                }}
                                            />
                                            {hasObservations && (
                                                <Tooltip title={observationTooltip} arrow>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'warning.main' }}
                                                        aria-label={t('phrases.observation')}
                                                        onClick={(e) => e.stopPropagation()}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Checkbox
                                                data-testid={`picture-${picture.imageId}-include-checkbox`}
                                                checked={imageMarkedToPdf(picture.imageId)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    onImageMarkedToPdf(picture.imageId, e.target.checked);
                                                }}
                                            />
                                            <Typography>{t('survey.includetopdf')}</Typography>
                                        </Stack>
                                    </Box>
                                </ImageListItem>
                            );
                        })}
                    </ImageList>
                ) : (
                    <span>{t('survey.noImages')}</span>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
